import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { authDebug, authDebugSessionSnapshot } from './auth-debug.util';

declare let PEP: any;

/** Injectable seams for unit tests (e.g. stub reload in Karma). */
export const authInitDeps = {
    reloadPage: (): void => {
        location.reload();
    }
};

/**
 * Returns true when OLCI has a usable PEP session (initialized flag or bearer token).
 * A site SWID cookie alone does not count — that caused 401 → reload loops on direct URLs.
 */
export function hasSessionSignal(authService: AuthService): boolean {
    return (
        authService.isInitialized.getValue() ||
        !!authService.accessToken
    );
}

/**
 * Entry point for screen components (Home, ReservationDetails). If the session is
 * already ready, invokes onReady immediately; otherwise delegates to waitForAuthThenLoadData.
 */
export function startLoadDataWhenReady(
    authService: AuthService,
    onReady: () => void,
    destroy$: Subject<void>,
    caller = 'unknown'
): void {
    authDebug(`startLoadDataWhenReady called from ${caller}`);
    authDebugSessionSnapshot('startLoadDataWhenReady session snapshot', authService);
    if (hasSessionSignal(authService)) {
        authDebug(`startLoadDataWhenReady: session ready — calling onReady immediately (${caller})`);
        onReady();
        return;
    }
    authDebug(`startLoadDataWhenReady: waiting for auth (${caller})`);
    waitForAuthThenLoadData(authService, onReady, destroy$, caller);
}

/**
 * Ensures PEP auth completes before the first API call. Probes PEP availability,
 * calls authService.initialize() once, and invokes onReady when the session is ready.
 * Handles the async PEP login path where initialize() resolves before the login event.
 */
export function waitForAuthThenLoadData(
    authService: AuthService,
    onReady: () => void,
    destroy$: Subject<void>,
    caller = 'unknown'
): void {
    authDebug(`waitForAuthThenLoadData entered (${caller})`);
    if (authService.isInitialized.getValue()) {
        authDebug(`waitForAuthThenLoadData: already initialized — onReady (${caller})`);
        onReady();
        return;
    }

    let authStarted = false;
    const startAuthOnce = (reason: string): void => {
        if (authStarted) {
            authDebug(`startAuthOnce skipped — already started (${reason})`, { caller });
            return;
        }
        authStarted = true;
        authDebug(`startAuthOnce: subscribing + calling initialize() (${reason})`, { caller });
        subscribeWhenInitialized(authService, onReady, destroy$, caller);
        authService.initialize().then(() => {
            authDebug(`initialize() promise resolved (${reason})`, { caller });
            authDebugSessionSnapshot('after initialize() resolved', authService);
            if (hasSessionSignal(authService)) {
                authDebug(`post-initialize hasSessionSignal true — calling onReady (${caller})`);
                onReady();
            } else {
                authDebug(`post-initialize hasSessionSignal false — waiting for isInitialized$ (${caller})`);
            }
        }).catch((err) => {
            authDebug(`initialize() promise rejected (${reason})`, { caller, err });
        });
    };

    const pepAvailable = typeof PEP !== 'undefined' && PEP?.PROFILE_AUTH;
    authDebug('PEP availability check', {
        caller,
        pepAvailable,
        pepType: typeof PEP
    });

    if (pepAvailable) {
        PEP.PROFILE_AUTH.getLoggedInStatus()
            .then((loggedIn: boolean) => {
                authDebug('PEP getLoggedInStatus resolved', { caller, loggedIn });
                startAuthOnce('pep-getLoggedInStatus-resolve');
            })
            .catch((err: unknown) => {
                authDebug('PEP getLoggedInStatus rejected — listen + fallback startAuth', { caller, err });
                listenForOneIdInit(authService, () => startAuthOnce('oneId-init-event'), destroy$, caller);
                startAuthOnce('pep-getLoggedInStatus-reject-fallback');
            });
        return;
    }

    authDebug(`PEP not available — listening for oneId-init (${caller})`);
    listenForOneIdInit(authService, () => startAuthOnce('oneId-init-no-pep'), destroy$, caller);
}

/**
 * Waits for authService.isInitialized$ to emit true, then calls onReady once.
 * Used when initialize() finishes before PEP delivers tokens via the async login event.
 */
function subscribeWhenInitialized(
    authService: AuthService,
    onReady: () => void,
    destroy$: Subject<void>,
    caller: string
): void {
    authDebug(`subscribeWhenInitialized: subscribing to isInitialized$ (${caller})`, {
        currentValue: authService.isInitialized.getValue()
    });
    authService.isInitialized
        .pipe(
            distinctUntilChanged(),
            filter(isInitialized => isInitialized === true),
            take(1),
            takeUntil(destroy$)
        )
        .subscribe(() => {
            authDebug(`isInitialized$ emitted true — calling onReady (${caller})`);
            authDebugSessionSnapshot('onReady via isInitialized$', authService);
            onReady();
        });
}

/**
 * Listens for parent-site navigation custom events (oneId-init, oneId-low-trust).
 * Starts auth when PEP becomes available on syndicated pages; reAuths on low-trust.
 * Removes the listener when destroy$ emits.
 */
function listenForOneIdInit(
    authService: AuthService,
    startAuth: () => void,
    destroy$: Subject<void>,
    caller: string
): void {
    authDebug(`listenForOneIdInit: attaching navigation listener (${caller})`);
    const handler = (event: CustomEvent): void => {
        const eventName = event.detail?.event;
        authDebug(`navigation custom event received (${caller})`, { eventName, detail: event.detail });
        if (eventName === 'oneId-init') {
            authDebug(`oneId-init received — starting auth (${caller})`);
            startAuth();
        } else if (eventName === 'oneId-low-trust') {
            authDebug(`oneId-low-trust received — reAuth (${caller})`);
            authService.reAuth()
                .then(() => {
                    authDebug(`oneId-low-trust reAuth succeeded (${caller})`);
                })
                .catch((err) => {
                    authDebug(`oneId-low-trust reAuth failed — reloading (${caller})`, { err });
                    authInitDeps.reloadPage();
                });
        } else {
            authDebug(`other navigation event (${caller})`, { eventName });
        }
    };

    document.addEventListener('navigation', handler as EventListener);

    destroy$.pipe(take(1)).subscribe(() => {
        authDebug(`listenForOneIdInit: removing navigation listener (${caller})`);
        document.removeEventListener('navigation', handler as EventListener);
    });
}
